import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://jbcentreprenoeren.dk';
const DEFAULT_IMAGE = `${SITE_URL}/images/og-image.jpg`;
const LOGO_URL = `${SITE_URL}/images/logo.png`;

const services = [
  { name: 'Jordvarme', url: '/jordvarme' },
  { name: 'Belægning', url: '/bel%C3%A6gning' },
  { name: 'Midlertidig vej', url: '/midlertidig-vej' },
  { name: 'Fundamenter', url: '/fundamenter' },
  { name: 'Tilbygning', url: '/tilbygning' },
  { name: 'LCA og bæredygtigt anlægsarbejde', url: '/lca' },
];

const businessData = {
  '@context': 'https://schema.org',
  '@type': 'GeneralContractor',
  '@id': `${SITE_URL}/#business`,
  name: 'JBC Entreprenøren',
  legalName: 'JBC Entreprenøren ApS',
  url: SITE_URL,
  logo: LOGO_URL,
  image: DEFAULT_IMAGE,
  description: 'Lokal entreprenør i Holbæk Kommune med anlæg, belægning, jordvarme, fundamenter og tilbygninger for private og erhverv på hele Sjælland siden 2018.',
  foundingDate: '2018',
  telephone: '+4522780476',
  email: 'jbc@jbcentreprenoeren.dk',
  vatID: 'DK44384345',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Naurvej 6, Undløse',
    addressLocality: 'Tølløse',
    postalCode: '4340',
    addressRegion: 'Region Sjælland',
    addressCountry: 'DK',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 55.6147,
    longitude: 11.7679,
  },
  areaServed: [
    { '@type': 'AdministrativeArea', name: 'Holbæk Kommune' },
    { '@type': 'AdministrativeArea', name: 'Sjælland' },
  ],
  sameAs: ['https://www.facebook.com/p/Jbc-Entrepern%C3%B8ren-100085100491591/'],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Entreprenørydelser',
    itemListElement: services.map((s) => ({
      '@type': 'Offer',
      itemOffered: { '@type': 'Service', name: s.name, url: `${SITE_URL}${s.url}` },
    })),
  },
};

const websiteData = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'JBC Entreprenøren',
  inLanguage: 'da-DK',
  publisher: { '@id': `${SITE_URL}/#business` },
};

const toAbsolute = (path) => {
  if (path.startsWith('http')) return path;
  return `${SITE_URL}${encodeURI(path)}`;
};

const SEOHead = ({
  title,
  description,
  image = DEFAULT_IMAGE,
  url = '/',
  structuredData = null,
  noindex = false,
  breadcrumbs = null,
  faq = null,
}) => {
  const fullTitle = title || 'JBC Entreprenøren – Entreprenør i Holbæk og på hele Sjælland';
  const canonicalUrl = toAbsolute(url);

  const breadcrumbData = breadcrumbs && breadcrumbs.length > 1 ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: toAbsolute(crumb.url),
    })),
  } : null;

  const faqData = faq ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faq.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  } : null;

  return (
    <Helmet>
      <html lang="da" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="JBC Entreprenøren" />
      <meta property="og:locale" content="da_DK" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {!noindex && (
        <script type="application/ld+json">{JSON.stringify(websiteData)}</script>
      )}
      {!noindex && (
        <script type="application/ld+json">{JSON.stringify(businessData)}</script>
      )}
      {breadcrumbData && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbData)}</script>
      )}
      {faqData && (
        <script type="application/ld+json">{JSON.stringify(faqData)}</script>
      )}
      {structuredData && (
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
